/**
 * @file previewLiveUsage.ts
 * @brief DEV ONLY: scripted `GET /billing/usage/live` answers for the
 *        recorder UI preview (`/__preview/boardroom-record?uiPreview=1&live=`),
 *        so the B5 line and notice can be checked without the backend.
 *
 * @details
 * `?live=free` Free, 2 minutes left, used up at 1 elapsed minute (the free
 * message) · `?live=plan` Pro, 2 left, then the plan message + price note ·
 * `?live=payg` pay as you go (no line) · `?live=404` backend without B5 ·
 * `?live=fair` Pro Notes at 52 of 60 h, used up at 1 elapsed minute (B6
 * message, no price) · `?live=fairok` Pro Notes under 80% (no line).
 * Polls every 15 s so the notice shows within a minute. Production builds
 * always get null (no stub).
 *
 * @author DES
 * @date 22-10-2026
 */

import { ApiClientError } from "@/shared/api/client";
import type { LiveUsageFetcher } from "../useLiveUsage";

const FREE_MESSAGE =
  "You've used your free minutes for this month. This recording keeps going and is free. New recordings start again on 1 November.";
const FAIR_MESSAGE =
  "You've used this month's 60 hours of Meeting Notes. This recording keeps going and is not charged. New Meeting Notes recordings start again on 15 November.";
const PLAN_MESSAGE = "Your plan's included Intelligence minutes for this month are used up. This meeting keeps recording.";

export function previewLiveUsageFetcher(kind: string | null): LiveUsageFetcher | null {
  if (!import.meta.env.DEV || !kind) return null;
  return async (_tier, elapsedSeconds) => {
    if (kind === "404") throw new ApiClientError("Not found", 404);
    if (kind === "payg") return { includedMinutesLeft: null, events: [], pollSeconds: 15 };
    if (kind === "fair" || kind === "fairok") {
      const usedSoFar = (kind === "fair" ? 3598 : 600) + Math.ceil(elapsedSeconds / 60);
      const fairUse = { used: usedSoFar, limit: 3600, resetsAt: "2026-11-15T00:00:00.000Z", blocked: usedSoFar >= 3600 };
      const hit = kind === "fair" && elapsedSeconds >= 60;
      return {
        pool: "notes_fair_use",
        fairUse,
        includedMinutesLeft: Math.max(0, 3600 - usedSoFar),
        events: hit
          ? [{ type: "included_minutes_used", atElapsedMinutes: 1, message: FAIR_MESSAGE, priceNote: null, nextRecordingBlocked: true }]
          : [],
        pollSeconds: 15,
      };
    }
    const elapsedMinutes = Math.ceil(elapsedSeconds / 60);
    const left = Math.max(0, 2 - elapsedMinutes);
    const used = elapsedSeconds >= 60;
    const message = kind === "free" ? FREE_MESSAGE : PLAN_MESSAGE;
    const priceNote = kind === "free" ? null : "Extra minutes are 1p each, added to your next plan bill.";
    return {
      includedMinutesLeftAtStart: 2,
      includedMinutesLeft: used ? 0 : left,
      events: used ? [{ type: "included_minutes_used", atElapsedMinutes: 1, message, priceNote }] : [],
      pollSeconds: 15,
    };
  };
}
